"use client";

import type { ComponentPropsWithRef, ReactNode } from "react";
import { cn } from "../../lib/utils";

export type ChoiceInputProps = Omit<ComponentPropsWithRef<"input">, "type"> & {
  type: "checkbox" | "radio";
};

/**
 * Native checkbox/radio behavior with a cross-browser custom visual.
 *
 * The input remains the interactive, focusable form control. The sibling
 * elements only render its visual state so labels, keyboard behavior and
 * submitted values keep working exactly like a native input.
 */
export function ChoiceInput({
  className,
  type,
  ...props
}: Readonly<ChoiceInputProps>): ReactNode {
  const isCheckbox = type === "checkbox";

  return (
    <span
      className={cn(
        "relative inline-flex size-[15px] shrink-0 align-middle",
        className
      )}
      data-slot={`${type}-control`}
    >
      <input
        className="peer absolute inset-0 z-10 m-0 size-full cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed forced-colors:appearance-auto forced-colors:opacity-100"
        type={type}
        {...props}
      />
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none box-border size-full border border-black bg-white transition-[background-color,border-color,outline-color] duration-150 ease-out peer-hover:bg-zinc-100 peer-focus-visible:outline-2 peer-focus-visible:outline-black peer-focus-visible:outline-offset-2 peer-active:bg-zinc-200 peer-disabled:border-zinc-400 peer-disabled:bg-zinc-100 forced-colors:hidden",
          isCheckbox ? "rounded-[2px]" : "rounded-full"
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute bg-black opacity-0 transition-opacity duration-150 ease-out peer-checked:opacity-100 peer-disabled:bg-zinc-400 forced-colors:hidden",
          isCheckbox ? "inset-[2px] rounded-[1px]" : "inset-[3px] rounded-full"
        )}
      />
    </span>
  );
}
